import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

import Types from '../constants/ActionTypes';
import Api from './Api';
import { log } from '../utils/logger';
import { wrapper } from '../utils/asyncFunctions';
import Languages from '../constants/Languages';
import { logAnalyticsEvent } from '../utils/logAnalyticsEvent';

export {
  selectLanguage,
  checkEmailOrPhone,
  clearEmailOrPhoneError,
  clearEnterEmailError,
  enterEmail,
  checkEmail,
  clearEnterEmail,
  emailVerified,
  unsubscribeEmail,
  skipEmail,
  createPassword,
  enterPassword,
  sendMagicLink,
  loginByMagicLink,
  resetPassword,
  enterPhone,
  clearEnterPhoneError,
  doNotAskAgain,
  closeOtpSentAlert,
  mergeUsers,
  onboardingCompleted,
  submitUser,
  getCurrentUser,
  updateUser,
  pushToken,
  login,
  logout,
  openDeleteAccount,
  closeDeleteAccount,
  deleteAccount,
  cancelCode,
  tryCode,
  sendOtp,
  getTerms,
  acceptTerms,
  updateReservationsAvailableQueue,
  submitFeedback,
  getPantries,
  searchPantries,
  clearSearch,
  fetchExplorePantries,
  setLocation,
  showSinglePantry,
  clearSinglePantry,
  openMakeAppointment,
  closeMakeAppointment,
  openMustVerify,
  closeMustVerify,
  openMustLogin,
  closeMustLogin,
  makeAppointment,
  openCancelAppointment,
  closeCancelAppointment,
  confirmCancelAppointment,
  openChangeAppointment,
  closeChangeAppointment,
  changeAppointment,
  openSelectEditCancelVisit,
  closeSelectEditCancelVisit,
  selectEditCancelVisit,
  closeReservationConfirmed,
  openScreenTefapAttestation,
  closeScreenTefapAttestation,
  openScreenMissingInfo,
  closeScreenMissingInfo,
  completeScreenTefapAttestation,
  setPantryFilter,
  getVisits,
  updateVisit,
  getUpdates,
  selectThread,
  backToUpdates,
  getTefapConfig,
  clearError,
  refreshRedux,
};

/**
 * Mobile devices may frequently have unreliable networks.
 * This means we always have to catch errors coming from fetch.
 * Otherwise the devices will throw exceptions and this is bad for our ranking.
 * So here we just log them and ignore the outcome.
 */
const logError = (error) => {
  console.log(error);
};

// helper to determine if user needs to go through onboarding or not
function onboardingComplete(user) {
  if (!user) return false;
  if (!user.first_name || !user.last_name) return false;
  if (user.first_name === '' || user.last_name === '') return false;
  if (!user.id) return false;
  return true;
}

// registration actions
function selectLanguage(index) {
  return function (dispatch) {
    logAnalyticsEvent('select_language');
    const language = Languages.languages[index];
    return Api.selectLanguage(index)
      .then(() => {
        // I spent hours trying to get the language switch work, and this finally
        // did it. Just dispatch it twice.
        // to solve this, it needs a big refactor I think. @todo @fix
        dispatch({ type: Types.SELECT_LANGUAGE_NEXT, data: language });
        dispatch({ type: Types.SELECT_LANGUAGE_NEXT, data: language });
      })
      .catch((e) => logError(e));
  };
}

function checkEmailOrPhone(searchString, deviceId, language, onSuccess) {
  return function (dispatch) {
    logAnalyticsEvent('check_phone_or_email');
    return Api.checkEmailOrPhone(searchString, language)
      .then((response) => {
        if (response) {
          const { results } = response;
          if (response.error && response.error == 'opted_out') {
            dispatch({ type: Types.EMAIL_OR_PHONE_ERROR, data: 'opted_out' });
          } else {
            dispatch({ type: Types.EMAIL_OR_PHONE, data: results });
            const { type, sms_enabled, email_valid, existing } = results;
            const flow = `${existing ? 'existing' : 'new'}_${type}`;
            if (type === 'phone') {
              if (sms_enabled) {
                dispatch({ type: Types.REGISTRATION_FLOW, data: flow });
                dispatch(sendOtp(searchString, deviceId, language));
                onSuccess('OtpCode');
              } else {
                dispatch({ type: Types.EMAIL_OR_PHONE_ERROR, data: 'no_sms_enabled_phone' });
              }
            } else {
              if (email_valid) {
                dispatch({ type: Types.REGISTRATION_FLOW, data: flow });
                if (existing) {
                  onSuccess('EnterPassword');
                } else {
                  AsyncStorage.setItem('lastUpdated', `${new Date().getTime()}`).then(() => {
                    AsyncStorage.setItem('token', results.token).then(() => {
                      dispatch({ type: Types.NEW_USER, data: response });
                      onSuccess('VerificationEmailSent');
                    });
                  });
                }
              } else {
                dispatch({ type: Types.EMAIL_OR_PHONE_ERROR, data: 'email_invalid' });
              }
            }
          }
        }
      })
      .catch((e) => logError(e));
  };
}

function clearEmailOrPhoneError() {
  return function (dispatch) {
    dispatch({ type: Types.CLEAR_EMAIL_OR_PHONE_ERROR });
  };
}

function enterEmail(email, onSuccess) {
  return function (dispatch) {
    const doSend = true;
    return Api.enterEmail(email, doSend)
      .then((response) => {
        if (response && response.error) {
          dispatch({ type: Types.ERROR, data: response.error });
        } else if (response && response.results) {
          const { results } = response;
          results.email = email;
          let error = null;
          if (!results.email_valid) {
            error = 'email_invalid';
          } else if (results.in_use) {
            error = 'email_in_use';
          }
          dispatch({ type: Types.ENTER_EMAIL, data: { results, error } });
          if (!error && onSuccess) {
            onSuccess();
          }
        }
      })
      .catch((e) => logError(e));
  };
}

function checkEmail(email, doSend, onSuccess) {
  return function (dispatch) {
    return Api.enterEmail(email, doSend)
      .then((response) => {
        if (response && response.error) {
          dispatch({ type: Types.ERROR, data: response.error });
        } else if (response && response.results) {
          const { results } = response;
          results.email = email;
          let error = null;
          if (!results.email_valid) {
            error = 'email_invalid';
          } else if (results.in_use) {
            error = 'email_in_use';
          }
          dispatch({ type: Types.ENTER_EMAIL, data: { results, error } });
          onSuccess();
        }
      })
      .catch((e) => logError(e));
  };
}

function clearEnterEmail() {
  return function (dispatch) {
    dispatch({ type: Types.CLEAR_ENTER_EMAIL });
  };
}

function clearEnterEmailError() {
  return function (dispatch) {
    dispatch({ type: Types.CLEAR_EMAIL_ERROR });
  };
}

function emailVerified(token, deviceId) {
  return function (dispatch) {
    const params = {
      token,
      device_os: Platform.OS,
      device_id: deviceId,
    };
    return Api.emailVerified(params)
      .then(async (response) => {
        if (!response || response.error) {
          dispatch({ type: Types.ERROR, data: response ? response.error : 'no response' });
        } else if (response.results && response.results.user) {
          const { results } = response;
          if (results.token) {
            await AsyncStorage.setItem('lastUpdated', `${new Date().getTime()}`);
            await AsyncStorage.setItem('token', results.token);
            await AsyncStorage.removeItem('sessionId');
          }
          dispatch({ type: Types.EMAIL_VERIFIED, data: results.user });
        }
      })
      .catch((e) => logError(e));
  };
}

function unsubscribeEmail(token, subscribe) {
  return function (dispatch) {
    return Api.unsubscribeEmail(token, subscribe)
      .then((response) => {
        if (response && response.status === 'success') {
          dispatch({ type: Types.UNSUBSCRIBE_EMAIL, data: response.results });
        }
      })
      .catch((e) => logError(e));
  };
}

function skipEmail() {
  return function (dispatch) {
    dispatch({ type: Types.SKIP_EMAIL });
  };
}

function createPassword(email, password, token, onSuccess) {
  return function (dispatch) {
    return Api.createPassword(email, password, token)
      .then((response) => {
        if (response) {
          const { results } = response;
          if (results && results.id) {
            dispatch({ type: Types.UPDATE_USER, data: results });
            onSuccess(results);
          } else if (results && results === 'reset') {
            onSuccess('reset');
          } else if (response.error) {
            dispatch({ type: Types.ERROR, data: response.error });
          }
        }
      })
      .catch((e) => logError(e));
  };
}

function enterPassword(email, password, language, onSuccess) {
  const languageCode = language && language.name ? language.name : null;
  return async function (dispatch) {
    const [error, response] = await wrapper(Api.enterPassword(email, password, languageCode));
    if (error || !response) {
      logError(error || 'no response');
      return;
    }

    await saveUserSessionToken(response, dispatch, onSuccess);
  };
}

const sendMagicLink = (email, magicLinkMethod, language, onSuccess) => async () => {
  const [error, response] = await wrapper(Api.sendMagicLink(email, magicLinkMethod, language));
  if (error || !response?.result) {
    logError(error || 'no response');
    return;
  }

  onSuccess(response);
};

async function saveUserSessionToken(response, dispatch, onSuccess) {
  if (response?.results?.user) {
    if (response?.results.token) {
      await AsyncStorage.setItem('lastUpdated', `${new Date().getTime()}`);
      await AsyncStorage.setItem('token', response?.results.token);
      await AsyncStorage.removeItem('sessionId');
    }
    dispatch({ type: Types.UPDATE_USER, data: response?.results.user });
    onSuccess(response?.results.user);
  } else if (response.error) {
    dispatch({ type: Types.ERROR, data: response.error });
  }
}

function loginByMagicLink(magicLinkToken, onSuccess) {
  return async function (dispatch) {
    const [error, response] = await wrapper(Api.loginByMagicLink(magicLinkToken));
    if (error || !response) {
      console.log(error || 'no response');
      return;
    }
    await saveUserSessionToken(response, dispatch, onSuccess);
  };
}

function resetPassword(email, onSuccess) {
  return function (dispatch) {
    Api.resetPassword(email)
      .then(() => {
        onSuccess();
      })
      .catch((e) => logError(e));
  };
}

function enterPhone(phone, onSuccess) {
  return function (dispatch) {
    return Api.enterPhone(phone)
      .then((response) => {
        if (response) {
          dispatch({ type: Types.ENTER_PHONE, data: { response, phone } });
          onSuccess(response);
        }
      })
      .catch((e) => logError(e));
  };
}

function clearEnterPhoneError() {
  return function (dispatch) {
    dispatch({ type: Types.CLEAR_PHONE_ERROR });
  };
}

function doNotAskAgain(type) {
  return function (dispatch) {
    return Api.doNotAskAgain(type)
      .then((response) => {
        if (response) {
          dispatch({ type: Types.UPDATE_USER, data: response });
        }
      })
      .catch((e) => logError(e));
  };
}

function closeOtpSentAlert() {
  return function (dispatch) {
    dispatch({ type: Types.CLOSE_OTP_SENT_ALERT });
  };
}

function submitUser(data, onSuccess) {
  return function (dispatch) {
    data.receive_notifications = true;
    return Api.createUser(data)
      .then((body) => {
        if (body) {
          if (body.user) {
            if (body.user.new) {
              logAnalyticsEvent('register_user');
              dispatch({ type: Types.NEW_USER, data: body });
              setTimeout(() => {
                onSuccess('FamilyDetails');
              }, 100);
            } else {
              AsyncStorage.setItem('lastUpdated', `${new Date().getTime()}`).then(() => {
                AsyncStorage.setItem('token', body.token).then(() => {
                  dispatch({ type: Types.EXISTING_USER, data: body.user });
                });
              });
            }
          } else {
            dispatch({ type: Types.SHOW_OTP, data: body });
          }
        }
      })
      .catch((error) => {
        dispatch({ type: Types.ERROR, error });
      });
  };
}

function getCurrentUser(onSuccess) {
  return function (dispatch) {
    Api.getCurrentUser()
      .then((response) => {
        if (response && response.user && response.user.id) {
          dispatch({ type: Types.LOGIN, data: response });
          if (onSuccess) onSuccess(response.user);
        }
      })
      .catch((e) => logError(e));
  };
}

function updateUser(updates, callback) {
  return function (dispatch) {
    return Api.updateUser(updates).then((response) => {
      if (response && response.user && response.user.id) {
        logAnalyticsEvent('update_user');
        dispatch({ type: Types.UPDATE_USER, data: response.user });
        if (callback) callback();
      }
    });
  };
}

function pushToken(token) {
  return function (dispatch) {
    return Api.pushToken(token)
      .then((response) => {
        if (response && response.status === 'success') {
          dispatch({ type: Types.EXPO_PUSH_TOKEN, data: { token } });
        }
      })
      .catch((e) => logError(e));
  };
}

function login(token) {
  return async function (dispatch) {
    // establish a session
    const [sError, sessionId] = await wrapper(Api.establishSession(token));
    if (sError) return dispatch({ type: Types.ERROR, data: sError });
    if (sessionId) {
      // get current user details
      const [uError, response] = await wrapper(Api.getCurrentUser());
      if (uError) return dispatch({ type: Types.ERROR, data: uError });
      if (response) {
        dispatch({ type: Types.LOGIN, data: response });
        logAnalyticsEvent('login');
      }
    }
  };
}

function logout(onSuccess) {
  return async function (dispatch) {
    await AsyncStorage.removeItem('token');
    await AsyncStorage.removeItem('sessionId');
    await AsyncStorage.removeItem('expiresAt');
    await AsyncStorage.removeItem('language');
    await AsyncStorage.removeItem('lastUpdated');
    await AsyncStorage.removeItem('onboarded');
    logAnalyticsEvent('logout');
    dispatch({ type: Types.LOGOUT });
    dispatch({ type: Types.PANTRY_LOGOUT });
    if (onSuccess) onSuccess();
  };
}

function openDeleteAccount() {
  return function (dispatch) {
    dispatch({ type: Types.OPEN_DELETE_ACCOUNT });
  };
}

function closeDeleteAccount() {
  return function (dispatch) {
    dispatch({ type: Types.CLOSE_DELETE_ACCOUNT });
  };
}

function deleteAccount(onSuccess) {
  return function (dispatch) {
    Api.deleteAccount()
      .then(() => {
        logAnalyticsEvent('delete_account');
        dispatch({ type: Types.CLOSE_DELETE_ACCOUNT });
        if (onSuccess) onSuccess();
      })
      .catch((e) => logError(e));
  };
}

function cancelCode() {
  return function (dispatch) {
    dispatch({ type: Types.CANCEL_OTP_CODE });
  };
}

function tryCode(phone, code, deviceId, language, user, onSuccess) {
  return async function (dispatch) {
    const needsSession = user && user.id;
    const languageCode = language && language.name ? language.name : null;
    let [error, body] = await wrapper(Api.otpConfirm(phone, code, deviceId, languageCode, needsSession));
    if (error) return dispatch({ type: Types.ERROR, data: error });
    if (body && body.token) {
      await AsyncStorage.setItem('token', body.token);
      await AsyncStorage.setItem('lastUpdated', `${new Date().getTime()}`);
      await AsyncStorage.removeItem('sessionId');
      // why do we request the pantries here??
      Api.getPantries().catch((e) => logError(e));
      if (body.user) {
        body.user.onboarding_complete = onboardingComplete(body.user);
        dispatch({ type: Types.CURRENT_USER, data: body.user });
        return onSuccess(body.user);
      }
      // we're going to create a user
      const user = {
        first_name: '',
        last_name: '',
        zip_code: '',
        phone,
        device_os: Platform.OS,
        device_id: deviceId,
        otp_verified: true,
        receive_notifications: true,
      };
      Languages.languages.forEach((langObject) => {
        if (language && language.name && langObject.name === language.name) {
          user.language = langObject.full;
        }
      });
      [error, body] = await wrapper(Api.createUser(user));
      if (error) return dispatch({ type: Types.ERROR, data: error });

      dispatch({ type: Types.NEW_USER, data: body });
      return onSuccess(body.user);
    }
    const tError = new Error('Could not verify token');
    return dispatch({ type: Types.ERROR, data: tError });
  };
}

function sendOtp(phone, deviceId, language) {
  return function (dispatch) {
    return Api.otpSend(phone, deviceId, language)
      .then(() => {
        logAnalyticsEvent('send_otp');
        dispatch({ type: Types.SEND_OTP, data: phone });
      })
      .catch((e) => logError(e));
  };
}

function mergeUsers(email, phone, onSuccess) {
  return function (dispatch) {
    return Api.mergeUsers(email, phone)
      .then((response) => {
        if (response) {
          const { results } = response;
          if (results && results.id) {
            dispatch({ type: Types.CURRENT_USER, data: results });
            onSuccess();
          }
        }
      })
      .catch((e) => logError(e));
  };
}

function getTerms(onSuccess) {
  return function (dispatch) {
    return Api.getTerms()
      .then((response) => {
        // returns docs for the logged-in user
        const okTypes = ['eula', 'privacy'];
        const docs = [];
        if (response && response.results && response.results.length) {
          response.results.forEach((term) => {
            if (term.type && okTypes.indexOf(term.type) !== -1) docs.push(term);
          });
        }
        onSuccess(docs.length);
        dispatch({ type: Types.TERMS_PRIVACY, data: docs });
      })
      .catch((e) => logError(e));
  };
}

function acceptTerms(onSuccess) {
  return function (dispatch) {
    return Api.acceptTerms()
      .then((response) => {
        if (response) {
          dispatch({ type: Types.ACCEPT_TERMS_PRIVACY, data: response.results });
          onSuccess(response.results);
        }
      })
      .catch((e) => logError(e));
  };
}

function onboardingCompleted(user, onSuccess) {
  return function (dispatch) {
    logAnalyticsEvent('registration_completed');
    AsyncStorage.setItem('onboarded', '1').then(() => {
      dispatch({ type: Types.ONBOARDING_COMPLETED, data: user });
      if (onSuccess) onSuccess();
    });
  };
}

function updateReservationsAvailableQueue(pantryId, params) {
  return function (dispatch) {
    return Api.updateReservationsAvailableQueue(pantryId, params)
      .then((response) => {
        if (response && response.pantry) {
          dispatch({ type: Types.RESERVATIONS_QUEUE, data: response.pantry });
        }
      })
      .catch((e) => logError(e));
  };
}

function submitFeedback(feedback) {
  return function (dispatch) {
    return Api.sendFeedback(feedback)
      .then(() => {
        dispatch({ type: Types.FEEDBACK });
      })
      .catch((e) => logError(e));
  };
}

// pantry actions
function getPantries(onSuccess, isLoggedIn, region) {
  return function (dispatch, getState) {
    const { filter } = getState().pantry;
    const { latitude, longitude, latitudeDelta, longitudeDelta } = region;
    // convert lat/lng + deltas into lat/lng of NW and SE corners
    const lat1 = (latitude + latitudeDelta).toFixed(2),
      lat2 = (latitude - latitudeDelta).toFixed(2),
      lng1 = (longitude - longitudeDelta).toFixed(2),
      lng2 = (longitude + longitudeDelta).toFixed(2);
    return Api.getPantries(isLoggedIn, lat1, lng1, lat2, lng2, filter)
      .then((pantries) => {
        if (pantries) {
          dispatch({ type: Types.LOAD_PANTRIES, data: { pantries, pantryRegion: region } });
        }
        if (onSuccess) onSuccess();
      })
      .catch((e) => logError(e));
  };
}

/**
 * Search pantries globally using Algolia (not bounded by map region)
 * @param {string} query - Search query text
 * @param {object} options - Optional parameters (limit, offset)
 * @param {boolean} isLoggedIn - Whether user is logged in
 */
function searchPantries(query, options = {}, isLoggedIn = false) {
  return function (dispatch, getState) {
    const { filter, location } = getState().pantry;
    const { limit = 100, offset = 0 } = options;

    dispatch({ type: Types.SEARCH_PANTRIES_REQUEST });

    // Prepare search options
    const searchOptions = {
      algoliaQuery: query,
      limit,
      offset,
    };

    // Add user location for distance-based sorting if available
    if (location && location.currentLocation && location.currentLocation.coords) {
      const { latitude, longitude } = location.currentLocation.coords;
      searchOptions.currentPosition = `${latitude},${longitude}`;
    }

    // Use configurable bounding box for global search
    // Backend currently requires bounding box parameters even for Algolia search
    const { getBoundingBoxParams } = require('../constants/SearchConfig');
    const { lat1, lat2, lng1, lng2 } = getBoundingBoxParams('US');

    return Api.getPantries(isLoggedIn, lat1, lng1, lat2, lng2, filter, searchOptions)
      .then((pantries) => {
        dispatch({
          type: Types.SEARCH_PANTRIES_SUCCESS,
          data: {
            results: pantries,
            total: pantries.length, // TODO: Get actual total from API response
            query,
          },
        });

        // Log search event (metadata only, no PII)
        logAnalyticsEvent('search_initiated', {
          query_length: query.length,
          result_count: pantries.length,
          has_user_location: !!searchOptions.currentPosition,
        });
      })
      .catch((error) => {
        logError(error);
        dispatch({
          type: Types.SEARCH_PANTRIES_FAILURE,
          data: {
            error: error.message || 'Search failed. Please try again.',
          },
        });
      });
  };
}

/**
 * Clear search state and return to explore mode
 */
function clearSearch() {
  return function (dispatch) {
    dispatch({ type: Types.CLEAR_SEARCH });
  };
}

/**
 * Fetch pantries within map bounding box (explore mode)
 * @param {object} region - Map region with latitude, longitude, and deltas
 * @param {boolean} isLoggedIn - Whether user is logged in
 */
function fetchExplorePantries(region, isLoggedIn = false) {
  return function (dispatch, getState) {
    const { filter } = getState().pantry;
    const { latitude, longitude, latitudeDelta, longitudeDelta } = region;

    dispatch({ type: Types.FETCH_EXPLORE_PANTRIES_REQUEST });

    // Convert lat/lng + deltas into lat/lng of NW and SE corners
    const lat1 = (latitude + latitudeDelta).toFixed(2),
      lat2 = (latitude - latitudeDelta).toFixed(2),
      lng1 = (longitude - longitudeDelta).toFixed(2),
      lng2 = (longitude + longitudeDelta).toFixed(2);

    const boundingBox = {
      north: parseFloat(lat1),
      south: parseFloat(lat2),
      east: parseFloat(lng2),
      west: parseFloat(lng1),
    };

    return Api.getPantries(isLoggedIn, lat1, lng1, lat2, lng2, filter)
      .then((pantries) => {
        dispatch({
          type: Types.FETCH_EXPLORE_PANTRIES_SUCCESS,
          data: {
            results: pantries,
            boundingBox,
          },
        });
      })
      .catch((error) => {
        logError(error);
        dispatch({
          type: Types.FETCH_EXPLORE_PANTRIES_FAILURE,
          data: {
            error: error.message || 'Failed to load pantries',
          },
        });
      });
  };
}

function setLocation(location, status) {
  return function (dispatch) {
    dispatch({ type: Types.SET_LOCATION, data: { currentLocation: location, status } });
  };
}

/**
 * Fetches pantry details by organization ID and dispatches and dispatches an action that will show the resulting
 * information to the user. Note that this fetches by organization ID, _not_ pantry ID (although the two may have
 * the same value).
 */
function showSinglePantry(organizationId, isLoggedIn) {
  log('Actions:showSinglePantry');
  return function (dispatch) {
    return Api.getPantryByOrganizationId(organizationId, isLoggedIn)
      .then((response) => {
        if (response && response.id) {
          dispatch({ type: Types.SHOW_PANTRY_DETAILS, data: response });
        }
      })
      .catch((e) => logError(e));
  };
}

function clearSinglePantry(onSuccess) {
  log('Actions:clearSinglePantry');
  return function (dispatch) {
    dispatch({ type: Types.CLOSE_PANTRY_DETAILS });
    if (onSuccess) onSuccess();
  };
}

function openMakeAppointment() {
  return function (dispatch) {
    dispatch({ type: Types.OPEN_MAKE_APPOINTMENT });
  };
}

function closeMakeAppointment() {
  return function (dispatch) {
    dispatch({ type: Types.CLOSE_MAKE_APPOINTMENT });
  };
}

function openMustVerify() {
  return function (dispatch) {
    dispatch({ type: Types.OPEN_MUST_VERIFY });
  };
}

function closeMustVerify() {
  return function (dispatch) {
    dispatch({ type: Types.CLOSE_MUST_VERIFY });
  };
}

function openMustLogin() {
  return function (dispatch) {
    dispatch({ type: Types.OPEN_MUST_LOGIN });
  };
}

function closeMustLogin() {
  return function (dispatch) {
    dispatch({ type: Types.CLOSE_MUST_LOGIN });
  };
}

function makeAppointment(pantryId, organizationId, serviceDate, time) {
  return async function (dispatch) {
    const [error, visit] = await wrapper(Api.makeAppointment(pantryId, serviceDate, time));
    if (error) return dispatch({ type: Types.ERROR, data: error });
    dispatch({ type: Types.CLOSE_MAKE_APPOINTMENT });
    if (visit) {
      await Api.getVisits()
        .then((visits) => {
          // Refresh pantry data from BE (note we must use org ID, not pantry ID)
          dispatch(showSinglePantry(organizationId, true));
          logAnalyticsEvent('make_reservation');
          dispatch({ type: Types.MAKE_APPOINTMENT, data: { visits, visit } });
        })
        .catch((e) => logError(e));
    }
  };
}

function openCancelAppointment(visit) {
  return function (dispatch) {
    dispatch({ type: Types.OPEN_CANCEL_APPOINTMENT, data: visit });
  };
}

function closeCancelAppointment() {
  return function (dispatch) {
    dispatch({ type: Types.CLOSE_CANCEL_APPOINTMENT });
  };
}

function confirmCancelAppointment(visitId, organizationId) {
  return async function (dispatch) {
    await Api.cancelVisit(visitId);
    const [error, visits] = await wrapper(Api.getVisits());
    if (error) return dispatch({ type: Types.ERROR, data: error });
    if (visits) {
      // Refresh pantry data from BE (note we must use org ID, not pantry ID)
      dispatch(showSinglePantry(organizationId, true));
      logAnalyticsEvent('cancel_reservation');
      dispatch({ type: Types.CONFIRM_CANCEL_APPOINTMENT, data: visits });
    }
  };
}

function openChangeAppointment(visit) {
  return function (dispatch) {
    Api.getNextOptions(visit.id)
      .then((results) => {
        if (results) {
          dispatch({ type: Types.OPEN_CHANGE_APPOINTMENT, data: results });
        }
      })
      .catch((e) => logError(e));
  };
}

function closeChangeAppointment() {
  return function (dispatch) {
    dispatch({ type: Types.CLOSE_CHANGE_APPOINTMENT });
  };
}

function changeAppointment(visitId, serviceDate, time, pantryId) {
  return async function (dispatch) {
    const [error, visit] = await wrapper(Api.changeAppointment(visitId, serviceDate, time));
    if (error) return dispatch({ type: Types.ERROR, data: error });
    if (visit) {
      await Api.getVisits()
        .then((visits) => {
          logAnalyticsEvent('change_reservation');
          dispatch({ type: Types.CLOSE_CHANGE_APPOINTMENT });
          setTimeout(() => {
            dispatch({ type: Types.MAKE_APPOINTMENT, data: { visits, visit } });
          }, 200);
        })
        .catch((e) => logError(e));
    }
  };
}

function openSelectEditCancelVisit(visits, editOrCancel) {
  return function (dispatch) {
    dispatch({ type: Types.OPEN_SELECT_EDIT_CANCEL, data: { visits, editOrCancel } });
  };
}

function closeSelectEditCancelVisit() {
  return function (dispatch) {
    dispatch({ type: Types.CLOSE_SELECT_EDIT_CANCEL });
  };
}

function selectEditCancelVisit(visit) {
  return function (dispatch) {
    Api.getNextOptions(visit.id)
      .then((results) => {
        if (results) {
          dispatch({ type: Types.CLOSE_SELECT_EDIT_CANCEL });
          // must close and re-open this for ios
          setTimeout(() => {
            dispatch({
              type: Types.SELECT_EDIT_CANCEL_VISIT,
              data: { visit, options: results },
            });
          }, 10);
        }
      })
      .catch((e) => logError(e));
  };
}

function closeReservationConfirmed() {
  return function (dispatch) {
    dispatch({ type: Types.CLOSE_RESERVATION_CONFIRMED });
  };
}

function openScreenTefapAttestation() {
  return function (dispatch) {
    dispatch({ type: Types.OPEN_SCREEN_TEFAP_ATTESTATION });
  };
}

function closeScreenTefapAttestation() {
  return function (dispatch) {
    dispatch({ type: Types.CLOSE_SCREEN_TEFAP_ATTESTATION });
  };
}

function openScreenMissingInfo() {
  return function (dispatch) {
    dispatch({ type: Types.OPEN_SCREEN_MISSING_INFO });
  };
}

function closeScreenMissingInfo() {
  return function (dispatch) {
    dispatch({ type: Types.CLOSE_SCREEN_MISSING_INFO });
  };
}

function completeScreenTefapAttestation(hasMissingInfo) {
  return function (dispatch) {
    dispatch({ type: Types.CLOSE_SCREEN_TEFAP_ATTESTATION });
    if (hasMissingInfo) {
      setTimeout(() => {
        dispatch({ type: Types.OPEN_SCREEN_MISSING_INFO });
      }, 100); // delay for smooth transition
    }
  };
}

function setPantryFilter(filter) {
  return function (dispatch) {
    dispatch({ type: Types.SET_PANTRY_FILTER, data: { filter } });
  };
}

// visit actions
function getVisits(onSuccess) {
  return function (dispatch) {
    return Api.getVisits()
      .then((visits) => {
        if (onSuccess) onSuccess();
        dispatch({ type: Types.GET_VISITS, data: visits });
      })
      .catch((e) => logError(e));
  };
}

function updateVisit(visit) {
  return function (dispatch) {
    dispatch({ type: Types.UPDATE_VISIT, data: visit });
  };
}

// update actions
function getUpdates(onSuccess) {
  return function (dispatch) {
    return Api.getUpdates()
      .then((response) => {
        if (response) {
          if (onSuccess) onSuccess();
          dispatch({ type: Types.GET_UPDATES, data: response.update_threads || [] });
        }
      })
      .catch((e) => logError(e));
  };
}

function selectThread(itemId) {
  return function (dispatch) {
    return Api.getThread(itemId)
      .then((response) => {
        if (response) {
          logAnalyticsEvent('open_thread');
          dispatch({
            type: Types.GET_THREAD,
            data: {
              id: itemId,
              updates: response.updates || [],
            },
          });
        }
      })
      .catch((e) => logError(e));
  };
}

function backToUpdates() {
  return function (dispatch) {
    dispatch({ type: Types.CLOSE_THREAD });
  };
}

function getTefapConfig() {
  return function (dispatch) {
    return Api.getTefapConfig()
      .then((tefapConfig) => {
        if (tefapConfig) {
          dispatch({ type: Types.LOAD_TEFAP_CONFIG, data: tefapConfig });
        }
      })
      .catch((e) => logError(e));
  };
}

function clearError() {
  return function (dispatch) {
    dispatch({ type: Types.ERROR, data: null });
  };
}

function refreshRedux() {
  log('Actions:refreshRedux');
  return function (dispatch) {
    dispatch({ type: Types.REFRESH });
  };
}
